import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, AlertTriangle, Car, Siren, Lightbulb, BellRing, Hand, ShieldAlert, Truck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { id: 'blocking', icon: Car, label: 'Blocking My Car', labelAr: 'يسد مخرجي', color: '#00C4B4', urgency: 'CRITICAL' },
  { id: 'emergency', icon: Siren, label: 'Emergency', labelAr: 'طوارئ', color: '#F04444', urgency: 'CRITICAL' },
  { id: 'accident', icon: AlertTriangle, label: 'Accident', labelAr: 'حادث', color: '#F04444', urgency: 'CRITICAL' },
  { id: 'towing', icon: Truck, label: 'Risk of Towing', labelAr: 'خطر السحب', color: '#FF6B35', urgency: 'HIGH' },
  { id: 'lights', icon: Lightbulb, label: 'Lights Left On', labelAr: 'الأضواء تعمل', color: '#F5A623', urgency: 'MEDIUM' },
  { id: 'alarm', icon: BellRing, label: 'Car Alarm', labelAr: 'إنذار السيارة', color: '#F5A623', urgency: 'MEDIUM' },
  { id: 'other', icon: Hand, label: 'Other', labelAr: 'أخرى', color: '#8B5CF6', urgency: 'LOW' },
];

const ScanPage = () => {
  const { scanId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('reason'); // reason → reveal → phone
  const [selectedReason, setSelectedReason] = useState(null);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [phoneData, setPhoneData] = useState(null);

  useEffect(() => {
    const fetchScanData = async () => {
      try {
        const res = await axios.get(`${API}/scan/${scanId}`);
        setVehicle(res.data);
      } catch {
        setError('This QR code is not valid or has expired.');
      } finally { setLoading(false); }
    };
    fetchScanData();
  }, [scanId]);

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setStep('reveal');
  };

  const handleRevealPhone = async () => {
    try {
      const res = await axios.post(`${API}/scan/${scanId}/contact`, { reason: selectedReason.id });
      setPhoneData(res.data);
      setPhoneRevealed(true);
      setStep('phone');
    } catch {
      setError('Unable to retrieve contact info.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#040F1E] flex items-center justify-center">
        <div className="inline-block w-10 h-10 border-4 border-[#00C4B4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#040F1E] flex items-center justify-center px-4">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="font-syne text-2xl font-bold text-white mb-2">Invalid QR Code</h1>
          <p className="text-white/60 font-dm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040F1E] text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700;800&family=Cairo:wght@400;600;700&display=swap');
        .font-syne { font-family: 'Syne', sans-serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
        .font-cairo { font-family: 'Cairo', sans-serif; }
      `}</style>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-syne text-4xl font-extrabold text-[#00C4B4] mb-1" style={{ letterSpacing: '-1.5px' }}>TAGO</h1>
          <p className="text-white/50 font-dm text-sm">Tag & Reach</p>
        </div>

        {/* Vehicle Info */}
        <div data-testid="scan-vehicle-info" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-5 mb-6 text-center">
          <Car className="w-10 h-10 text-[#00C4B4] mx-auto mb-2" />
          <h2 className="font-syne text-xl font-bold text-white">{vehicle?.label}</h2>
          <p className="text-white/50 text-sm font-dm">Vehicle Owner Tag</p>
        </div>

        {/* Step: Reason Selection */}
        {step === 'reason' && (
          <div data-testid="reason-selection">
            <h3 className="font-syne text-lg font-bold text-white mb-1 text-center">Why are you contacting?</h3>
            <p className="text-white/50 text-sm text-center mb-6 font-dm font-cairo" dir="rtl">لماذا تتواصل مع صاحب السيارة؟</p>

            <div className="space-y-3">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  data-testid={`reason-${r.id}`}
                  onClick={() => handleReasonSelect(r)}
                  className="w-full flex items-center gap-4 bg-[#0C2040] border-2 rounded-xl p-4 hover:scale-[1.02] transition-all text-left"
                  style={{ borderColor: `${r.color}30` }}
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${r.color}15` }}>
                    <r.icon className="w-5 h-5" style={{ color: r.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-dm font-semibold text-white text-sm">{r.label}</p>
                    <p className="font-cairo text-white/40 text-xs" dir="rtl">{r.labelAr}</p>
                  </div>
                  <span className="text-xs font-bold px-2 py-1 rounded-full" style={{
                    background: `${r.color}15`, color: r.color, border: `1px solid ${r.color}30`
                  }}>
                    {r.urgency}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step: Reveal Phone */}
        {step === 'reveal' && selectedReason && (
          <div data-testid="reveal-step" className="text-center">
            <div className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-6 mb-6">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: `${selectedReason.color}15` }}>
                <selectedReason.icon className="w-7 h-7" style={{ color: selectedReason.color }} />
              </div>
              <p className="font-dm font-semibold text-white mb-1">Reason: {selectedReason.label}</p>
              <p className="text-white/50 text-sm font-dm">Tap below to reveal the owner's phone number</p>
            </div>

            <button
              data-testid="reveal-phone-button"
              onClick={handleRevealPhone}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold text-lg rounded-xl transition-all hover:shadow-lg hover:shadow-[#00C4B4]/30"
            >
              <Phone className="w-5 h-5" />
              Tap to Reveal Phone Number
            </button>

            <button onClick={() => { setStep('reason'); setSelectedReason(null); }} className="mt-4 text-white/40 text-sm hover:text-white/60 font-dm transition-colors">
              Change reason
            </button>
          </div>
        )}

        {/* Step: Phone Revealed */}
        {step === 'phone' && phoneData && (
          <div data-testid="phone-revealed" className="text-center">
            <div className="bg-[#0C2040] border-2 border-[#00C4B4]/40 rounded-2xl p-8 mb-6">
              <div className="w-16 h-16 bg-[#00C4B4]/10 border-2 border-[#00C4B4]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-[#00C4B4]" />
              </div>
              <p className="text-white/50 text-sm font-dm mb-2">Vehicle Owner's Number</p>
              <p data-testid="revealed-phone-number" className="font-syne text-3xl font-extrabold text-white tracking-wide">
                {phoneData.country_code} {phoneData.phone}
              </p>
            </div>

            <a
              data-testid="call-now-button"
              href={`tel:${phoneData.country_code}${phoneData.phone}`}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-lg rounded-xl transition-all hover:shadow-lg mb-3"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>

            <a
              data-testid="whatsapp-button"
              href={`https://wa.me/${phoneData.country_code.replace('+', '')}${phoneData.phone}?text=Hi, I scanned your TAGO QR code. Reason: ${selectedReason?.label}`}
              target="_blank" rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] hover:bg-[#1da855] text-white font-bold text-lg rounded-xl transition-all hover:shadow-lg"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10">
          <p className="text-white/30 text-xs font-dm">Powered by <span className="text-[#00C4B4]">TAGO</span></p>
        </div>
      </div>
    </div>
  );
};

export default ScanPage;
